import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Save, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

import { payerApi } from '../lib/api/payerApi';

// Local minimal payer type aligned with Admin Payers list/edit shape
type PayerItem = {
  PayerAccountId: string;
  Name: string;
  EmailAddress?: string;
  ExternalId1?: string;
  ExternalId2?: string;
  CreatedDate?: string;
};

// Local edit request type per Admin Payers edit schema
type AdminEditPayerRequest = {
  PayerAccountId?: string;
  Name?: string | null;
  EmailAddress?: string | null;
  ExternalId1?: string | null;
  ExternalId2?: string | null;
};

interface PayerEditModalProps {
  payer: PayerItem | null;
  isOpen: boolean;
  onClose: () => void;
}

export const PayerEditModal: React.FC<PayerEditModalProps> = ({
  payer,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<AdminEditPayerRequest>({});

  // Initialize form data when payer changes
  useEffect(() => {
    if (payer) {
      setFormData({
        Name: payer.Name,
        EmailAddress: payer.EmailAddress ?? undefined,
        ExternalId1: payer.ExternalId1 ?? undefined,
        ExternalId2: payer.ExternalId2 ?? undefined,
      });
    }
  }, [payer]);

  // Edit payer mutation
  const editMutation = useMutation({
    mutationFn: ({ payerId, request }: { payerId: string; request: AdminEditPayerRequest }) =>
      // Cast to any to satisfy current API client types while aligning with Admin schema
      payerApi.editPayer(payerId, request as any),
    onSuccess: () => {
      toast.success('Payer updated successfully');
      queryClient.invalidateQueries({ queryKey: ['payers'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to update payer: ${error.response?.data?.ErrorMessage || error.message}`);
    },
  });

  const handleInputChange = (field: keyof AdminEditPayerRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!payer) return;

    // Build full request including all fields (not only changed ones)
    const fullRequest: AdminEditPayerRequest = {
      PayerAccountId: payer.PayerAccountId,
      Name: formData.Name ?? payer.Name ?? null,
      EmailAddress: formData.EmailAddress ?? payer.EmailAddress ?? null,
      ExternalId1: formData.ExternalId1 ?? payer.ExternalId1 ?? null,
      ExternalId2: formData.ExternalId2 ?? payer.ExternalId2 ?? null,
    };

    editMutation.mutate({ payerId: payer.PayerAccountId, request: fullRequest });
  };

  const handleClose = () => {
    setFormData({});
    onClose();
  };

  if (!payer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Payer</DialogTitle>
          <DialogDescription>
            Update payer information for {payer.Name} (ID: {payer.PayerAccountId})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Payer Details</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="payerAccountId">Payer Account ID</Label>
                <Input
                  id="payerAccountId"
                  value={payer.PayerAccountId}
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.Name ?? ''}
                  onChange={(e) => handleInputChange('Name', e.target.value)}
                  placeholder="Enter payer name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.EmailAddress ?? ''}
                  onChange={(e) => handleInputChange('EmailAddress', e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="externalId1">External ID 1</Label>
                <Input
                  id="externalId1"
                  value={formData.ExternalId1 ?? ''}
                  onChange={(e) => handleInputChange('ExternalId1', e.target.value)}
                  placeholder="Enter external ID 1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="externalId2">External ID 2</Label>
                <Input
                  id="externalId2"
                  value={formData.ExternalId2 ?? ''}
                  onChange={(e) => handleInputChange('ExternalId2', e.target.value)}
                  placeholder="Enter external ID 2"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button type="submit" disabled={editMutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {editMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
